import React from 'react';
import { motion } from 'framer-motion';
import {
  Thermometer, Droplets, Activity, Battery, Footprints
} from 'lucide-react';
import { useMQTT } from '../context/MQTTContext';
import SensorCard from '../components/v2/SensorCard';
import AIRiskPanel from '../components/v2/AIRiskPanel';
import GPSLiveCard from '../components/v2/GPSLiveCard';
import { computeRisk } from '../lib/ai';
import { Link } from 'react-router-dom';

export default function DashboardPage() {
  const { sensorData, connected } = useMQTT();
  const { score, level } = computeRisk(sensorData);

  return (
    <div className="grid gap-4 sm:gap-5">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl p-5 sm:p-7 text-white"
        style={{
          background:
            'linear-gradient(135deg,#6366f1 0%,#8b5cf6 45%,#ec4899 100%)'
        }}
      >
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/20 blur-2xl" />
        <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest opacity-80">
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </p>
            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight mt-1">
              Welcome back.
            </h1>
            <p className="text-sm sm:text-base opacity-90 mt-1 max-w-md">
              Your container is being monitored in realtime.
              {connected ? ' Streaming live data.' : ' Reconnecting to the broker...'}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Link to="/live"
                className="px-4 py-2 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur text-sm font-semibold transition">
                Live Monitoring →
              </Link>
              <Link to="/ai"
                className="px-4 py-2 rounded-full bg-white text-indigo-600 hover:bg-indigo-50 text-sm font-bold transition">
                AI Insights
              </Link>
            </div>
          </div>
          <div className="hidden sm:block text-right">
            <p className="text-xs uppercase tracking-widest opacity-80">Risk Score</p>
            <p className="text-5xl font-extrabold tabular-nums leading-none mt-1">{score}</p>
            <p className="text-xs font-bold uppercase mt-1 opacity-90">{level}</p>
          </div>
        </div>
      </motion.div>

      {/* AI compact */}
      <AIRiskPanel compact />

      {/* GPS live tracking — full width on mobile, spans 2 cols on larger */}
      <GPSLiveCard wide />

      {/* Sensor grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <SensorCard
          title="Temperature" metric="temperature"
          value={sensorData.temperature} unit="°C"
          icon={Thermometer} color="rose"
          formatter={(v) => v.toFixed(1)}
          threshold={{ safe: 38, crit: 45 }}
        />
        <SensorCard
          title="Humidity" metric="humidity"
          value={sensorData.humidity} unit="%"
          icon={Droplets} color="sky"
          formatter={(v) => v.toFixed(0)}
          threshold={{ safe: 85, crit: 95 }}
        />
        <SensorCard
          title="Vibration" metric="vibration"
          value={sensorData.vibration} unit="m/s²"
          icon={Activity} color="fuchsia"
          formatter={(v) => v.toFixed(2)}
          threshold={{ safe: 11.5, crit: 15 }}
        />
        <SensorCard
          title="Battery" metric="battery"
          value={sensorData.battery} unit="%"
          icon={Battery} color="emerald"
          formatter={(v) => v.toFixed(0)}
          threshold={{ safe: 30, crit: 15 }}
        />
        <SensorCard
          title="Motion" metric="motion"
          value={sensorData.motion} unit=""
          icon={Footprints} color="violet"
          formatter={(v) => (v ? 'Detected' : 'Still')}
        />
        <Link to="/analytics" className="card-surface ring-1 ring-white/40 p-4 sm:p-5 grid place-items-center text-center hover:-translate-y-1 transition-transform">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider opacity-60">View</p>
            <p className="text-lg font-extrabold mt-1">Analytics →</p>
            <p className="text-xs opacity-60 mt-1">Trends, history, charts</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
